import { widgetStyles } from './styles';
import { getConfig } from '../core/config';
import {
  getStore,
  setState,
  removeSelectedElement,
  clearSelectedElements,
  resetStore,
  subscribe,
} from '../core/state';
import {
  createOverlayElements,
  removeOverlayElements,
  clearAllOutlines,
  updateSelectionOutlines,
} from './overlay';
import { activateSelector, deactivateSelector, isSelectorActive, setSelectionListener } from './selector';
import { destroyBugWidgetDebug, initBugWidgetDebug, logBugWidgetDebug } from './debug';
import { getConsoleLogs } from '../collectors/console';
import { getNetworkRequests } from '../collectors/network';
import { getErrors } from '../collectors/errors';
import { getPerformanceData } from '../collectors/performance';
import { getEnvironment } from '../collectors/environment';
import { getDomSnapshot } from '../collectors/dom';
import { sanitizeString } from '../utils/sanitize';
import { compressScreenshot, enforcePayloadLimit } from '../utils/truncate';

const LEMON_LOGO = '<svg width="24" height="24" viewBox="0 0 160 162" fill="none" xmlns="http://www.w3.org/2000/svg" style="vertical-align:middle;margin-right:6px"><rect x="82.78" y="25.53" width="20.73" height="20.73" rx="2.76" fill="#FFC233"/><rect x="111.28" y="12.05" width="15.55" height="15.55" rx="2.83" fill="#FFC233"/><rect x="93.67" y="56.62" width="22.8" height="22.8" rx="2.07" fill="#FFC233"/><rect x="129.95" y="39" width="24.88" height="24.88" rx="2.07" fill="#FFC233"/><path d="M71.57 85.66c0 2.75 2.23 4.99 4.99 4.99h76.49c3.47 0 5.95 3.34 4.72 6.59-4.82 12.66-12.87 26.38-22.8 36.32-26.59 26.59-64.12 34.28-93.27 21.82C25.88 161.58 13.06 162.03 6.3 155.27c-6.76-6.76-6.31-19.58.21-35.38C-5.74 90.73 1.96 53.2 28.03 26.62c9.85-9.85 24.2-19.35 36.97-24.63 3.22-1.33 6.57 1.14 6.57 4.62v78.05z" fill="#FFC233"/></svg>';

let hostEl: HTMLDivElement | null = null;
let iframeEl: HTMLIFrameElement | null = null;
let tokenPromptEl: HTMLDivElement | null = null;
let mobileSelectStripEl: HTMLDivElement | null = null;
let unsubscribe: (() => void) | null = null;
let activeMobileTouch: { id: number; x: number; y: number; startedAt: number } | null = null;
let mobileTapBurstTimestamps: number[] = [];
let mobileViewport = false;

// Saved scroll/overflow state
let savedBodyOverflow = '';
let savedHtmlOverflow = '';

function lockScroll(): void {
  savedBodyOverflow = document.body.style.overflow;
  savedHtmlOverflow = document.documentElement.style.overflow;
  document.body.style.overflow = 'hidden';
  document.documentElement.style.overflow = 'hidden';
}

function unlockScroll(): void {
  document.body.style.overflow = savedBodyOverflow;
  document.documentElement.style.overflow = savedHtmlOverflow;
}

// Form state
let includeScreenshot = true;
let advancedOpen = true;
let panelSide: 'right' | 'left' = 'right';
let manualAttachmentError = '';
let manualAttachmentId = 0;
type FormDraft = {
  type: string;
  title: string;
  description: string;
  steps: string;
  expected: string;
  severity: string;
};

const DEFAULT_FORM_DRAFT: FormDraft = {
  type: 'bug',
  title: '',
  description: '',
  steps: '',
  expected: '',
  severity: 'medium',
};

let formDraft: FormDraft = { ...DEFAULT_FORM_DRAFT };

type ManualAttachment = {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  previewUrl: string;
};

let manualAttachments: ManualAttachment[] = [];

const TOKEN_KEY = 'citrus_widget_token';
const MOBILE_MEDIA_QUERY = '(hover: none) and (pointer: coarse)';
const MOBILE_TAP_TRIGGER_COUNT = 5;
const MOBILE_TAP_TRIGGER_WINDOW_MS = 1200;
const MOBILE_TAP_MAX_DURATION_MS = 280;
const MOBILE_TAP_MAX_MOVEMENT_PX = 20;
const MAX_MANUAL_ATTACHMENT_COUNT = 5;
const MAX_MANUAL_FILE_BYTES = 10 * 1024 * 1024; // 10MB
const MAX_MANUAL_IMAGE_BYTES = 2 * 1024 * 1024; // 2MB per uploaded image after compression

function getStoredToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

function storeToken(token: string): void {
  try { localStorage.setItem(TOKEN_KEY, token); } catch {}
}

function clearStoredToken(): void {
  try { localStorage.removeItem(TOKEN_KEY); } catch {}
}

function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.(MOBILE_MEDIA_QUERY).matches) return true;
  if ((navigator.maxTouchPoints || 0) > 1) return true;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

function isMobileViewportLayout(): boolean {
  return isMobileDevice() || window.innerWidth <= 768;
}

function getHiddenTransform(): string {
  if (mobileViewport) return 'translateY(100%)';
  return panelSide === 'right' ? 'translateX(100%)' : 'translateX(-100%)';
}

function syncMobileSelectionUi(): void {
  const mobileSelecting = mobileViewport && isSelectorActive();
  if (iframeEl) {
    iframeEl.style.pointerEvents = mobileSelecting ? 'none' : 'auto';
  }
  if (mobileSelectStripEl) {
    mobileSelectStripEl.style.display = mobileSelecting ? 'flex' : 'none';
  }
}

function applyIframeLayout(): void {
  if (!iframeEl) return;

  if (mobileViewport) {
    iframeEl.style.left = '0';
    iframeEl.style.right = '0';
    iframeEl.style.bottom = '0';
    iframeEl.style.width = '100vw';
    iframeEl.style.maxWidth = '100vw';
    iframeEl.style.borderLeft = 'none';
    iframeEl.style.borderRight = 'none';
    iframeEl.style.borderTop = 'none';
    iframeEl.style.borderBottom = 'none';

    if (window.visualViewport) {
      iframeEl.style.top = `${Math.max(0, Math.round(window.visualViewport.offsetTop))}px`;
      iframeEl.style.height = `${Math.round(window.visualViewport.height)}px`;
    } else {
      iframeEl.style.top = '0';
      iframeEl.style.height = '100dvh';
    }
  } else {
    iframeEl.style.top = '0';
    iframeEl.style.bottom = 'auto';
    iframeEl.style.height = '100vh';
    iframeEl.style.width = '420px';
    iframeEl.style.maxWidth = '100vw';
    iframeEl.style.right = panelSide === 'right' ? '0' : 'auto';
    iframeEl.style.left = panelSide === 'left' ? '0' : 'auto';
    iframeEl.style.borderLeft = panelSide === 'right' ? '1px solid #e4e4e7' : 'none';
    iframeEl.style.borderRight = panelSide === 'left' ? '1px solid #e4e4e7' : 'none';
    iframeEl.style.borderTop = 'none';
    iframeEl.style.borderBottom = 'none';
  }

  iframeEl.style.transform = getStore().state === 'idle' ? getHiddenTransform() : 'translate(0,0)';
  syncMobileSelectionUi();
}

function scheduleIframeTransitionEnable(): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (!iframeEl) return;
      iframeEl.style.transition = 'transform 0.35s cubic-bezier(0.4,0,0.2,1)';
    });
  });
}

function handleViewportChange(): void {
  const previous = mobileViewport;
  mobileViewport = isMobileViewportLayout();

  if (previous !== mobileViewport && mobileViewport && isSelectorActive()) {
    deactivateSelector();
    setState('form');
  }

  applyIframeLayout();
  if (previous !== mobileViewport) {
    renderPanel();
  }
  syncMobileSelectionUi();
}

function toggleWidgetWithTokenGate(): void {
  if (!getStoredToken()) {
    if (tokenPromptEl) {
      tokenPromptEl.remove();
      tokenPromptEl = null;
    } else {
      showTokenPrompt();
    }
    return;
  }

  const store = getStore();
  if (store.state === 'idle') {
    openPanel();
  } else {
    closePanel();
  }
}

function openWidgetWithTokenGate(): void {
  if (!getStoredToken()) {
    if (!tokenPromptEl) showTokenPrompt();
    return;
  }
  if (getStore().state === 'idle') {
    openPanel();
  }
}

function resetMobileTapBurst(): void {
  mobileTapBurstTimestamps = [];
  activeMobileTouch = null;
}

function recordMobileTap(now: number): void {
  if (mobileTapBurstTimestamps.length > 0 && now - mobileTapBurstTimestamps[0] > MOBILE_TAP_TRIGGER_WINDOW_MS) {
    mobileTapBurstTimestamps = [now];
    return;
  }

  mobileTapBurstTimestamps.push(now);
  if (mobileTapBurstTimestamps.length !== MOBILE_TAP_TRIGGER_COUNT) return;

  const firstTapAt = mobileTapBurstTimestamps[0];
  const isRapidBurst = now - firstTapAt <= MOBILE_TAP_TRIGGER_WINDOW_MS;
  resetMobileTapBurst();
  if (isRapidBurst) {
    openWidgetWithTokenGate();
  }
}

function handleMobileTouchStart(event: TouchEvent): void {
  if (event.touches.length !== 1) {
    resetMobileTapBurst();
    return;
  }

  const touch = event.touches[0];
  activeMobileTouch = {
    id: touch.identifier,
    x: touch.clientX,
    y: touch.clientY,
    startedAt: Date.now(),
  };
}

function handleMobileTouchEnd(event: TouchEvent): void {
  if (!activeMobileTouch) {
    resetMobileTapBurst();
    return;
  }

  if (event.changedTouches.length !== 1 || event.touches.length !== 0) {
    resetMobileTapBurst();
    return;
  }

  const touch = event.changedTouches[0];
  if (touch.identifier !== activeMobileTouch.id) {
    resetMobileTapBurst();
    return;
  }

  const duration = Date.now() - activeMobileTouch.startedAt;
  const dx = touch.clientX - activeMobileTouch.x;
  const dy = touch.clientY - activeMobileTouch.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  activeMobileTouch = null;

  if (duration > MOBILE_TAP_MAX_DURATION_MS || distance > MOBILE_TAP_MAX_MOVEMENT_PX) {
    resetMobileTapBurst();
    return;
  }

  recordMobileTap(Date.now());
}

function handleMobileTouchCancel(): void {
  resetMobileTapBurst();
}

function initMobileTapTrigger(): void {
  if (!isMobileDevice()) return;
  document.addEventListener('touchstart', handleMobileTouchStart, { passive: true });
  document.addEventListener('touchend', handleMobileTouchEnd, { passive: true });
  document.addEventListener('touchcancel', handleMobileTouchCancel, { passive: true });
}

function destroyMobileTapTrigger(): void {
  document.removeEventListener('touchstart', handleMobileTouchStart);
  document.removeEventListener('touchend', handleMobileTouchEnd);
  document.removeEventListener('touchcancel', handleMobileTouchCancel);
  resetMobileTapBurst();
}

async function verifyToken(token: string): Promise<{ valid: boolean; label?: string }> {
  const config = getConfig();
  try {
    const res = await fetch(`${config.endpoint.replace(/\/api\/.*$/, '')}/api/bugs/verify-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Api-Key': config.apiKey },
      body: JSON.stringify({ token }),
    });
    return res.ok ? await res.json() : { valid: false };
  } catch { return { valid: false }; }
}

function showTokenPrompt(): void {
  if (tokenPromptEl) tokenPromptEl.remove();

  const config = getConfig();
  tokenPromptEl = document.createElement('div');
  tokenPromptEl.setAttribute('data-br-widget', '');
  Object.assign(tokenPromptEl.style, {
    position: 'fixed',
    zIndex: '2147483647',
    pointerEvents: 'auto',
  });
  // Position based on config
  if (config.position === 'bottom-right' || !config.position) {
    tokenPromptEl.style.bottom = '24px';
    tokenPromptEl.style.right = '24px';
  } else if (config.position === 'bottom-left') {
    tokenPromptEl.style.bottom = '24px';
    tokenPromptEl.style.left = '24px';
  } else if (config.position === 'top-right') {
    tokenPromptEl.style.top = '24px';
    tokenPromptEl.style.right = '24px';
  } else if (config.position === 'top-left') {
    tokenPromptEl.style.top = '24px';
    tokenPromptEl.style.left = '24px';
  }

  tokenPromptEl.innerHTML = `
    <div style="background:#fff;border:1px solid #e4e4e7;border-radius:12px;padding:20px;width:260px;box-shadow:0 8px 32px rgba(0,0,0,0.12);font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;color:#18181b;">
      <div style="font-size:15px;font-weight:600;margin-bottom:8px;"><span>&#128027;</span> Bug Reporter</div>
      <p style="font-size:12px;color:#71717a;margin:0 0 12px 0;">Enter your access code to activate</p>
      <input class="br-token-input" type="text" placeholder="BL-XXXX-XXXX" maxlength="12" style="width:100%;padding:10px 12px;border-radius:6px;border:1px solid #e4e4e7;background:#fff;color:#18181b;font-size:14px;font-family:monospace;letter-spacing:1px;text-transform:uppercase;box-sizing:border-box;outline:none;margin-bottom:8px;" />
      <div class="br-token-error" style="display:none;color:#ef4444;font-size:12px;margin-bottom:8px;"></div>
      <button class="br-token-submit" style="width:100%;padding:10px 20px;border-radius:6px;border:none;background:#18181b;color:white;font-size:14px;font-weight:500;cursor:pointer;font-family:inherit;">Activate</button>
    </div>
  `;
  document.body.appendChild(tokenPromptEl);

  const input = tokenPromptEl.querySelector('.br-token-input') as HTMLInputElement;
  const btn = tokenPromptEl.querySelector('.br-token-submit') as HTMLButtonElement;
  const err = tokenPromptEl.querySelector('.br-token-error') as HTMLDivElement;

  async function doActivate() {
    const val = input.value.trim().toUpperCase();
    if (!val) return;
    btn.textContent = 'Checking...';
    btn.disabled = true;
    const result = await verifyToken(val);
    if (result.valid) {
      storeToken(val);
      tokenPromptEl?.remove();
      tokenPromptEl = null;
    } else {
      err.textContent = 'Invalid access code';
      err.style.display = 'block';
      btn.textContent = 'Activate';
      btn.disabled = false;
    }
  }

  btn.addEventListener('click', doActivate);
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') doActivate(); });
}

function getIframeDoc(): Document | null {
  return iframeEl?.contentDocument || null;
}

function updateFormDraft(patch: Partial<FormDraft>): void {
  formDraft = {
    ...formDraft,
    ...patch,
  };
}

function getDraftPatch(data: unknown): Partial<FormDraft> | null {
  if (!data || typeof data !== 'object') return null;
  const raw = data as Record<string, unknown>;
  const patch: Partial<FormDraft> = {};

  if (typeof raw.type === 'string') patch.type = raw.type || 'bug';
  if (typeof raw.title === 'string') patch.title = raw.title;
  if (typeof raw.description === 'string') patch.description = raw.description;
  if (typeof raw.steps === 'string') patch.steps = raw.steps;
  if (typeof raw.expected === 'string') patch.expected = raw.expected;
  if (typeof raw.severity === 'string') patch.severity = raw.severity || 'medium';

  return patch;
}

function captureFormDraftFromDoc(doc: Document | null): void {
  if (!doc) return;

  const typeEl = doc.getElementById('br-type') as HTMLSelectElement | null;
  const titleEl = doc.getElementById('br-title') as HTMLInputElement | null;
  const descEl = doc.getElementById('br-description') as HTMLTextAreaElement | null;
  const stepsEl = doc.getElementById('br-steps') as HTMLTextAreaElement | null;
  const expectedEl = doc.getElementById('br-expected') as HTMLTextAreaElement | null;
  const severityEl = doc.getElementById('br-severity') as HTMLSelectElement | null;

  updateFormDraft({
    type: typeEl ? typeEl.value || 'bug' : formDraft.type,
    title: titleEl ? titleEl.value : formDraft.title,
    description: descEl ? descEl.value : formDraft.description,
    steps: stepsEl ? stepsEl.value : formDraft.steps,
    expected: expectedEl ? expectedEl.value : formDraft.expected,
    severity: severityEl ? severityEl.value || 'medium' : formDraft.severity,
  });
}

function restoreFormDraftToDoc(doc: Document | null): void {
  if (!doc) return;

  const typeEl = doc.getElementById('br-type') as HTMLSelectElement | null;
  const titleEl = doc.getElementById('br-title') as HTMLInputElement | null;
  const descEl = doc.getElementById('br-description') as HTMLTextAreaElement | null;
  const stepsEl = doc.getElementById('br-steps') as HTMLTextAreaElement | null;
  const expectedEl = doc.getElementById('br-expected') as HTMLTextAreaElement | null;
  const severityEl = doc.getElementById('br-severity') as HTMLSelectElement | null;

  if (typeEl) typeEl.value = formDraft.type || 'bug';
  if (titleEl) titleEl.value = formDraft.title;
  if (descEl) descEl.value = formDraft.description;
  if (stepsEl) stepsEl.value = formDraft.steps;
  if (expectedEl) expectedEl.value = formDraft.expected;
  if (severityEl) severityEl.value = formDraft.severity || 'medium';
}

function buildSrcdoc(): string {
  const store = getStore();
  const elements = store.selectedElements;
  const selecting = isSelectorActive();
  const mobile = mobileViewport;

  let bodyContent = '';

  if (store.state === 'success') {
    bodyContent = `
      <div class="br-panel open ${mobile ? 'mobile' : ''}">
        <div class="br-panel-header">
          <h2>${LEMON_LOGO} Report a Bug</h2>
          <div style="display:flex;gap:6px;">
            ${mobile ? '' : `<button class="br-close-btn" id="br-toggle-side" title="Move to ${panelSide === 'right' ? 'left' : 'right'}"><svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><rect x="1" y="2" width="14" height="12" rx="2"/><line x1="${panelSide === 'right' ? '6' : '10'}" y1="2" x2="${panelSide === 'right' ? '6' : '10'}" y2="14"/></svg></button>`}
            <button class="br-close-btn" id="br-close">&#x2715;</button>
          </div>
        </div>
        <div class="br-panel-body">
          <div class="br-success">
            <div class="br-success-icon">&#10004;</div>
            <h3>Report Submitted!</h3>
            <p>Thank you for your feedback. The full report has been logged to the console.</p>
            <button class="br-btn br-btn-primary" id="br-done" style="width:auto; flex:none; padding:10px 32px;">Done</button>
          </div>
        </div>
      </div>`;
  } else if (store.state === 'submitting') {
    bodyContent = `
      <div class="br-panel open ${mobile ? 'mobile' : ''}">
        <div class="br-panel-header">
          <h2>${LEMON_LOGO} Report a Bug</h2>
        </div>
        <div class="br-panel-body">
          <div class="br-success">
            <div class="br-spinner"></div>
            <h3 style="margin-top: 20px">Preparing report...</h3>
            <p>Capturing screenshots and collecting diagnostics</p>
          </div>
        </div>
      </div>`;
  } else if (mobile && selecting) {
    bodyContent = `
      <div class="br-panel open mobile br-mobile-selecting"></div>`;
  } else {
    bodyContent = `
      <div class="br-panel open ${mobile ? 'mobile' : ''}">
        <div class="br-panel-header">
          <h2>${LEMON_LOGO} Report a Bug</h2>
          <div style="display:flex;gap:6px;">
            ${mobile ? '' : `<button class="br-close-btn" id="br-toggle-side" title="Move to ${panelSide === 'right' ? 'left' : 'right'}"><svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><rect x="1" y="2" width="14" height="12" rx="2"/><line x1="${panelSide === 'right' ? '6' : '10'}" y1="2" x2="${panelSide === 'right' ? '6' : '10'}" y2="14"/></svg></button>`}
            <button class="br-close-btn" id="br-close">&#x2715;</button>
          </div>
        </div>

        <div class="br-panel-body">
          ${mobile ? '' : `${selecting ? `
            <div class="br-select-banner">
              <span>&#127919;</span>
              Click elements on the page to select them
              <button id="br-stop-select">Done</button>
            </div>
          ` : `
            <div class="br-select-banner">
              <span>&#128433;</span>
              <span style="flex:1">Element selection paused</span>
              <button id="br-start-select">Select Elements</button>
            </div>
          `}`}

          <div class="br-field">
            <label>Type <span class="br-required">*</span></label>
            <select class="br-select" id="br-type">
              <option value="bug" selected>Bug</option>
              <option value="feature">Feature Request</option>
              <option value="ui">UI Issue</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div class="br-field">
            <label>Title <span class="br-required">*</span></label>
            <input class="br-input" id="br-title" type="text" placeholder="Short summary of the issue" maxlength="200" />
          </div>

          <div class="br-field">
            <label>Description <span class="br-required">*</span></label>
            <textarea class="br-textarea" id="br-description" placeholder="Describe the issue in detail..." rows="3"></textarea>
          </div>

          <div class="br-elements-section">
            <div class="br-elements-header">
              <span>Selected Elements</span>
              ${elements.length > 0 ? `<span class="br-elements-count">${elements.length}</span>` : ''}
            </div>
            ${elements.length > 0 ? `
              <ul class="br-elements-list">
                ${elements.map((el, i) => `
                  <li class="br-element-item">
                    <span class="br-element-selector" title="${escapeAttr(el.selector)}">${escapeHtml(shortenSelector(el.selector))}</span>
                    <button class="br-element-remove" data-index="${i}" title="Remove">&#x2715;</button>
                  </li>
                `).join('')}
              </ul>
            ` : `
              <div class="br-no-elements">No elements selected. Use ${mobile ? 'Select element' : 'selection mode'} to attach one.</div>
            `}
          </div>

          <label class="br-screenshot-toggle" id="br-screenshot-toggle">
            <div class="br-toggle-switch ${includeScreenshot ? 'active' : ''}" id="br-toggle-indicator"></div>
            <span class="br-screenshot-label">Attach viewport screenshot</span>
          </label>

          <div class="br-upload-section">
            <div class="br-upload-header">
              <span>Manual screenshots</span>
              ${manualAttachments.length > 0 ? `<span class="br-upload-count">${manualAttachments.length}</span>` : ''}
            </div>

            <label class="br-upload-trigger ${mobile ? 'mobile' : ''}">
              <input class="br-file-input" id="br-manual-files" type="file" accept="image/*" multiple />
              <span>Add image${mobile ? '' : 's'} from device</span>
            </label>
            <p class="br-upload-help">Images only. Up to ${MAX_MANUAL_ATTACHMENT_COUNT} files, ${formatFileSize(MAX_MANUAL_FILE_BYTES)} each.</p>
            ${manualAttachmentError ? `<p class="br-upload-error">${escapeHtml(manualAttachmentError)}</p>` : ''}

            ${manualAttachments.length > 0 ? `
              <ul class="br-upload-chips">
                ${manualAttachments.map((item) => `
                  <li class="br-upload-chip">
                    <img class="br-upload-thumb" src="${escapeAttr(item.previewUrl)}" alt="" />
                    <span class="br-upload-name" title="${escapeAttr(item.name)}">${escapeHtml(item.name)}</span>
                    <span class="br-upload-size">${formatFileSize(item.size)}</span>
                    <button class="br-upload-remove" data-manual-id="${escapeAttr(item.id)}" title="Remove image">&#x2715;</button>
                  </li>
                `).join('')}
              </ul>
            ` : `
              <div class="br-upload-empty">No manual images selected.</div>
            `}
          </div>

          <button class="br-advanced-toggle ${advancedOpen ? 'open' : ''}" id="br-advanced-btn">
            <span class="br-chevron">&#9654;</span>
            Advanced
          </button>

          <div class="br-advanced-content ${advancedOpen ? 'open' : ''}">
            <div class="br-field">
              <label>Steps to Reproduce</label>
              <textarea class="br-textarea" id="br-steps" placeholder="1. Go to page X&#10;2. Click on Y&#10;3. Observe Z" rows="3"></textarea>
            </div>

            <div class="br-field">
              <label>Expected Behavior</label>
              <textarea class="br-textarea" id="br-expected" placeholder="What should have happened..." rows="2"></textarea>
            </div>

            <div class="br-field">
              <label>Severity</label>
              <select class="br-select" id="br-severity">
                <option value="low">Low</option>
                <option value="medium" selected>Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>

          ${mobile ? `<div class="br-mobile-signout-wrap"><button class="br-signout" id="br-signout">Sign out</button></div>` : ''}
        </div>

        <div class="br-panel-footer ${mobile ? 'mobile' : ''}">
          ${mobile
            ? `<button class="br-btn br-btn-secondary" id="br-start-select">Select element</button>
               <button class="br-btn br-btn-primary" id="br-submit">Submit</button>`
            : `<button class="br-btn br-btn-secondary" id="br-cancel">Cancel</button>
               <button class="br-btn br-btn-primary" id="br-submit">Submit Report &#128640;</button>`}
        </div>

        ${mobile ? '' : `<div style="text-align:center"><button class="br-signout" id="br-signout">Sign out</button></div>`}
      </div>`;
  }

  // Build the script that wires up events inside the iframe and posts messages to parent
  const script = `
    <script>
      function collectDraftData() {
        return {
          type: (document.getElementById('br-type') || {}).value || 'bug',
          title: (document.getElementById('br-title') || {}).value || '',
          description: (document.getElementById('br-description') || {}).value || '',
          steps: (document.getElementById('br-steps') || {}).value || '',
          expected: (document.getElementById('br-expected') || {}).value || '',
          severity: (document.getElementById('br-severity') || {}).value || 'medium',
        };
      }

      document.addEventListener('click', function(e) {
        var target = e.target;
        var id = target.id || (target.closest && target.closest('[id]') || {}).id;

        if (id === 'br-toggle-side') {
          window.parent.postMessage({ type: 'br-toggle-side' }, '*');
        } else if (id === 'br-close' || id === 'br-cancel') {
          window.parent.postMessage({ type: 'br-close' }, '*');
        } else if (id === 'br-done') {
          window.parent.postMessage({ type: 'br-close' }, '*');
        } else if (id === 'br-stop-select') {
          window.parent.postMessage({ type: 'br-deactivate-selector' }, '*');
        } else if (id === 'br-start-select') {
          window.parent.postMessage({ type: 'br-activate-selector', data: collectDraftData() }, '*');
        } else if (id === 'br-signout') {
          window.parent.postMessage({ type: 'br-signout' }, '*');
        } else if (id === 'br-submit') {
          var data = collectDraftData();
          window.parent.postMessage({ type: 'br-submit', data: data }, '*');
        } else if (id === 'br-screenshot-toggle' || (target.closest && target.closest('#br-screenshot-toggle'))) {
          window.parent.postMessage({ type: 'br-toggle-screenshot' }, '*');
        } else if (id === 'br-advanced-btn' || (target.closest && target.closest('#br-advanced-btn'))) {
          window.parent.postMessage({ type: 'br-toggle-advanced' }, '*');
        } else if (target.classList && target.classList.contains('br-element-remove')) {
          var index = parseInt(target.getAttribute('data-index') || '0');
          window.parent.postMessage({ type: 'br-remove-element', index: index }, '*');
        } else if (target.classList && target.classList.contains('br-upload-remove')) {
          var removeId = target.getAttribute('data-manual-id');
          if (removeId) {
            window.parent.postMessage({ type: 'br-remove-manual-image', id: removeId }, '*');
          }
        } else if (target.closest && target.closest('.br-upload-remove')) {
          var removeBtn = target.closest('.br-upload-remove');
          var removeIdFromClosest = removeBtn && removeBtn.getAttribute('data-manual-id');
          if (removeIdFromClosest) {
            window.parent.postMessage({ type: 'br-remove-manual-image', id: removeIdFromClosest }, '*');
          }
        }
      });

      var fileInput = document.getElementById('br-manual-files');
      if (fileInput) {
        fileInput.addEventListener('change', function(ev) {
          var input = ev.currentTarget;
          var files = input && input.files ? Array.from(input.files) : [];
          if (files.length > 0) {
            window.parent.postMessage({ type: 'br-manual-files', files: files }, '*');
          }
          if (input) input.value = '';
        });
      }

      document.addEventListener('input', function() {
        window.parent.postMessage({ type: 'br-form-draft', data: collectDraftData() }, '*');
      }, true);

      document.addEventListener('change', function() {
        window.parent.postMessage({ type: 'br-form-draft', data: collectDraftData() }, '*');
      }, true);
    <\/script>
  `;

  return `<!DOCTYPE html>
<html>
<head>
  <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
  <style>${widgetStyles}</style>
</head>
<body style="margin:0;padding:0;">
${bodyContent}
${script}
</body>
</html>`;
}

function createMobileSelectStrip(): void {
  if (mobileSelectStripEl) return;
  const strip = document.createElement('div');
  strip.setAttribute('data-br-widget', '');
  strip.style.cssText = [
    'position:fixed',
    'left:12px',
    'right:12px',
    'top:calc(env(safe-area-inset-top) + 10px)',
    'min-height:44px',
    'display:none',
    'align-items:center',
    'gap:10px',
    'justify-content:space-between',
    'padding:8px 12px',
    'border-radius:12px',
    'background:rgba(24, 24, 27, 0.94)',
    'color:#fafafa',
    'font-size:14px',
    'box-shadow:0 8px 24px rgba(0,0,0,0.24)',
    'z-index:2147483647',
    'pointer-events:auto',
    'user-select:none',
    '-webkit-user-select:none',
  ].join(';');

  const label = document.createElement('span');
  label.textContent = 'Tap element • Cancel';

  const btn = document.createElement('button');
  btn.textContent = 'Cancel';
  btn.style.cssText = [
    'min-height:32px',
    'padding:6px 10px',
    'border-radius:8px',
    'border:none',
    'background:#ffffff',
    'color:#18181b',
    'font-size:12px',
    'font-weight:600',
    'font-family:inherit',
    'cursor:pointer',
    'user-select:none',
    '-webkit-user-select:none',
  ].join(';');

  btn.addEventListener('click', () => {
    deactivateSelector('mobile_strip_cancel');
    setState('form');
    renderPanel();
  });

  strip.appendChild(label);
  strip.appendChild(btn);
  document.body.appendChild(strip);
  mobileSelectStripEl = strip;
  syncMobileSelectionUi();
}

function removeMobileSelectStrip(): void {
  mobileSelectStripEl?.remove();
  mobileSelectStripEl = null;
}

function renderPanel(): void {
  if (!iframeEl) return;

  captureFormDraftFromDoc(getIframeDoc());

  const srcdoc = buildSrcdoc();
  iframeEl.setAttribute('srcdoc', srcdoc);

  // Restore form values after iframe loads
  iframeEl.onload = () => {
    restoreFormDraftToDoc(getIframeDoc());
  };
  syncMobileSelectionUi();
}

function onElementSelected(): void {
  logBugWidgetDebug('selection_listener_invoked', { mobileViewport, selectorActive: isSelectorActive() });
  if (!mobileViewport) return;
  if (!isSelectorActive()) return;
  logBugWidgetDebug('mobile_return_to_form_callback_fired');
  deactivateSelector();
  setState('form');
}

export function createWidget(): void {
  initBugWidgetDebug();
  mobileViewport = isMobileViewportLayout();

  hostEl = document.createElement('div');
  hostEl.id = 'bl-bug-reporter';
  hostEl.setAttribute('data-br-widget', '');
  hostEl.style.cssText = 'position:fixed;top:0;right:0;width:0;height:0;z-index:2147483647;pointer-events:none;';

  // Create overlay elements (outside iframe, on main page)
  createOverlayElements();
  createMobileSelectStrip();

  // Create iframe for panel
  iframeEl = document.createElement('iframe');
  iframeEl.setAttribute('data-br-widget', '');
  iframeEl.style.cssText = 'position:fixed;top:0;right:0;width:420px;height:100vh;border:none;z-index:2147483647;pointer-events:auto;transition:none;';
  iframeEl.style.transform = getHiddenTransform();
  iframeEl.setAttribute('srcdoc', buildSrcdoc());
  hostEl.appendChild(iframeEl);

  document.body.appendChild(hostEl);

  // Listen for messages from iframe
  window.addEventListener('message', handleIframeMessage);

  // Subscribe to state changes
  unsubscribe = subscribe(() => renderPanel());

  // Keyboard shortcut
  document.addEventListener('keydown', handleShortcut);
  window.addEventListener('resize', handleViewportChange);
  window.visualViewport?.addEventListener('resize', handleViewportChange);
  window.visualViewport?.addEventListener('scroll', handleViewportChange);
  setSelectionListener(onElementSelected);
  initMobileTapTrigger();
  applyIframeLayout();
  syncMobileSelectionUi();
  scheduleIframeTransitionEnable();
}

export function destroyWidget(): void {
  document.removeEventListener('keydown', handleShortcut);
  window.removeEventListener('message', handleIframeMessage);
  window.removeEventListener('resize', handleViewportChange);
  window.visualViewport?.removeEventListener('resize', handleViewportChange);
  window.visualViewport?.removeEventListener('scroll', handleViewportChange);
  setSelectionListener(null);
  destroyMobileTapTrigger();
  deactivateSelector('destroy_widget');
  removeOverlayElements();
  removeMobileSelectStrip();
  clearAllOutlines();
  clearManualAttachments();
  hostEl?.remove();
  unsubscribe?.();
  hostEl = null;
  iframeEl = null;
  destroyBugWidgetDebug();
}

function handleIframeMessage(e: MessageEvent): void {
  if (!e.data || typeof e.data.type !== 'string') return;
  // Only accept messages from our iframe
  if (iframeEl && e.source !== iframeEl.contentWindow) return;

  switch (e.data.type) {
    case 'br-close':
      closePanel();
      break;
    case 'br-deactivate-selector':
      deactivateSelector('iframe_deactivate_selector');
      setState('form');
      break;
    case 'br-activate-selector':
      {
        const patch = getDraftPatch(e.data.data);
        if (patch) {
          updateFormDraft(patch);
        } else {
          captureFormDraftFromDoc(getIframeDoc());
        }
      }
      activateSelector();
      setState('selecting');
      break;
    case 'br-signout':
      clearStoredToken();
      closePanel();
      showTokenPrompt();
      break;
    case 'br-submit':
      {
        const patch = getDraftPatch(e.data.data);
        if (patch) {
          updateFormDraft(patch);
        }
      }
      handleSubmit(e.data.data);
      break;
    case 'br-manual-files':
      handleManualFiles(e.data.files);
      break;
    case 'br-remove-manual-image':
      if (typeof e.data.id === 'string') {
        removeManualAttachment(e.data.id);
      }
      break;
    case 'br-toggle-screenshot':
      includeScreenshot = !includeScreenshot;
      renderPanel();
      break;
    case 'br-toggle-advanced':
      advancedOpen = !advancedOpen;
      renderPanel();
      break;
    case 'br-toggle-side':
      panelSide = panelSide === 'right' ? 'left' : 'right';
      applyIframeLayout();
      renderPanel();
      break;
    case 'br-remove-element': {
      const store = getStore();
      const el = store.selectedElements[e.data.index];
      if (el) {
        removeSelectedElement(el.element);
        updateSelectionOutlines(store.selectedElements.map(s => s.element));
      }
      break;
    }
    case 'br-form-draft':
      {
        const patch = getDraftPatch(e.data.data);
        if (patch) {
          updateFormDraft(patch);
        }
      }
      break;
  }
}

function handleShortcut(e: KeyboardEvent): void {
  const isMac = navigator.platform.toUpperCase().includes('MAC');
  const modifier = isMac ? e.metaKey : e.ctrlKey;
  if (modifier && e.shiftKey && e.key.toUpperCase() === 'B') {
    e.preventDefault();
    e.stopPropagation();
    toggleWidgetWithTokenGate();
  }
}

export function openPanel(): void {
  const store = getStore();
  if (store.state !== 'idle') return;
  lockScroll();
  if (mobileViewport) {
    setState('form');
  } else {
    setState('selecting');
    activateSelector();
  }
  applyIframeLayout();
  renderPanel();
}

export function closePanel(): void {
  captureFormDraftFromDoc(getIframeDoc());
  unlockScroll();
  deactivateSelector('close_panel');
  clearSelectedElements();
  clearAllOutlines();
  // Hide iframe by sliding it out
  if (iframeEl) {
    iframeEl.style.transform = getHiddenTransform();
  }
  resetStore();
  advancedOpen = true;
  includeScreenshot = true;
  clearManualAttachments();
  panelSide = 'right';
  formDraft = { ...DEFAULT_FORM_DRAFT };
  if (iframeEl && !mobileViewport) {
    iframeEl.style.right = '0';
    iframeEl.style.left = 'auto';
  }
}

async function handleSubmit(formData: { type: string; title: string; description: string; steps: string; expected: string; severity: string }): Promise<void> {
  const store = getStore();
  const config = getConfig();

  const title = formData.title.trim();
  const description = formData.description.trim();

  // Validate
  if (!title || !description) {
    // Mark invalid fields in iframe
    const doc = getIframeDoc();
    if (doc) {
      if (!title) doc.getElementById('br-title')?.classList.add('invalid');
      if (!description) doc.getElementById('br-description')?.classList.add('invalid');
    }
    return;
  }

  deactivateSelector();
  setState('submitting');

  // Capture screenshots
  let screenshots = { viewport: '', elements: [] as string[] };
  if (includeScreenshot) {
    const { captureAllScreenshots } = await import('./screenshot');
    screenshots = await captureAllScreenshots(store.selectedElements);
  }
  const manualScreenshots = await buildManualScreenshotPayload();

  // Build payload
  const elements = store.selectedElements.map((el, i) => ({
    selector: el.selector,
    xpath: el.xpath,
    tag: el.tag,
    classes: el.classes,
    id: el.id,
    text: el.text,
    boundingBox: el.boundingBox,
    computedStyles: el.computedStyles,
    accessibility: el.accessibility,
    screenshot: screenshots.elements[i] || '',
  }));

  const domSnapshot = getDomSnapshot(store.selectedElements);

  let payload: Record<string, unknown> = {
    version: '1.0',
    app: config.app,
    apiKey: config.apiKey,
    timestamp: new Date().toISOString(),
    report: {
      type: formData.type || 'bug',
      title: sanitizeString(title, 200),
      description: sanitizeString(description),
      severity: formData.severity || 'medium',
      stepsToReproduce: sanitizeString(formData.steps || ''),
      expectedBehavior: sanitizeString(formData.expected || ''),
    },
    elements,
    screenshots: {
      viewport: screenshots.viewport,
      elements: screenshots.elements,
      manual: manualScreenshots,
    },
    diagnostics: {
      console: getConsoleLogs(),
      network: getNetworkRequests(),
      errors: getErrors(),
      performance: getPerformanceData(),
    },
    environment: getEnvironment(),
    dom: domSnapshot,
    metadata: {
      ...config.metadata,
      ...store.metadata,
      custom: store.context,
    },
  };

  payload = await enforcePayloadLimit(payload);

  if (config.beforeSubmit) {
    payload = config.beforeSubmit(payload) as Record<string, unknown>;
  }

  const widgetToken = getStoredToken();
  try {
    const apiBase = config.endpoint.replace(/\/api\/.*$/, '');
    const res = await fetch(`${apiBase}/api/bugs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Api-Key': config.apiKey,
        ...(widgetToken ? { 'X-Widget-Token': widgetToken } : {}),
      },
      body: JSON.stringify({
        title: (payload.report as Record<string, unknown>).title,
        type: (payload.report as Record<string, unknown>).type,
        severity: (payload.report as Record<string, unknown>).severity,
        description: (payload.report as Record<string, unknown>).description,
        elements: payload.elements,
        screenshots: payload.screenshots,
        diagnostics: payload.diagnostics,
        environment: payload.environment,
        dom: payload.dom,
        metadata: payload.metadata,
      }),
    });
    if (!res.ok) {
      console.error('[BugReporter] Submit failed:', res.status);
    }
  } catch (err) {
    console.error('[BugReporter] Submit error:', err);
  }

  const payloadSizeKb = Math.round(new TextEncoder().encode(JSON.stringify(payload)).length / 1024);
  console.log('%c[BugReporter] Report submitted', 'color: #6366f1; font-weight: bold; font-size: 14px');
  console.log({
    title: (payload.report as Record<string, unknown>).title,
    severity: (payload.report as Record<string, unknown>).severity,
    payloadSizeKb,
    selectedElements: elements.length,
    viewportScreenshot: Boolean((payload.screenshots as Record<string, unknown>).viewport),
    elementScreenshots: Array.isArray((payload.screenshots as Record<string, unknown>).elements)
      ? ((payload.screenshots as Record<string, unknown>).elements as unknown[]).length
      : 0,
    manualScreenshots: Array.isArray((payload.screenshots as Record<string, unknown>).manual)
      ? ((payload.screenshots as Record<string, unknown>).manual as unknown[]).length
      : 0,
    consoleEntries: Array.isArray((payload.diagnostics as Record<string, unknown>).console)
      ? ((payload.diagnostics as Record<string, unknown>).console as unknown[]).length
      : 0,
    networkEntries: Array.isArray((payload.diagnostics as Record<string, unknown>).network)
      ? ((payload.diagnostics as Record<string, unknown>).network as unknown[]).length
      : 0,
  });

  if (config.onSubmit) {
    config.onSubmit(payload);
  }

  setState('success');
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeAttr(str: string): string {
  return str.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function shortenSelector(selector: string): string {
  if (selector.length <= 40) return selector;
  const parts = selector.split(' > ');
  if (parts.length <= 2) return selector.slice(0, 40) + '...';
  return parts[0] + ' > ... > ' + parts[parts.length - 1];
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function clearManualAttachments(): void {
  for (const attachment of manualAttachments) {
    try {
      URL.revokeObjectURL(attachment.previewUrl);
    } catch {
      // noop
    }
  }
  manualAttachments = [];
  manualAttachmentError = '';
}

function removeManualAttachment(id: string): void {
  const attachment = manualAttachments.find(item => item.id === id);
  if (attachment) {
    try {
      URL.revokeObjectURL(attachment.previewUrl);
    } catch {
      // noop
    }
  }
  manualAttachments = manualAttachments.filter(item => item.id !== id);
  manualAttachmentError = '';
  renderPanel();
}

function handleManualFiles(filesInput: unknown): void {
  const files = Array.isArray(filesInput) ? filesInput.filter((item): item is File => item instanceof File) : [];
  if (files.length === 0) return;

  const errors: string[] = [];
  for (const file of files) {
    if (!file.type.startsWith('image/')) {
      errors.push(`"${file.name}" is not an image.`);
      continue;
    }

    if (file.size > MAX_MANUAL_FILE_BYTES) {
      errors.push(`"${file.name}" is too large (${formatFileSize(file.size)}). Max is ${formatFileSize(MAX_MANUAL_FILE_BYTES)}.`);
      continue;
    }

    if (manualAttachments.length >= MAX_MANUAL_ATTACHMENT_COUNT) {
      errors.push(`You can attach up to ${MAX_MANUAL_ATTACHMENT_COUNT} images.`);
      break;
    }

    const id = `manual-${++manualAttachmentId}`;
    manualAttachments.push({
      id,
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      previewUrl: URL.createObjectURL(file),
    });
  }

  manualAttachmentError = errors.join(' ');
  renderPanel();
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string) || '');
    reader.onerror = () => reject(reader.error || new Error('Failed to read image file.'));
    reader.readAsDataURL(file);
  });
}

async function buildManualScreenshotPayload(): Promise<string[]> {
  const images: string[] = [];

  for (const attachment of manualAttachments) {
    try {
      const dataUrl = await readFileAsDataUrl(attachment.file);
      if (!dataUrl) continue;
      const compressed = await compressScreenshot(dataUrl, MAX_MANUAL_IMAGE_BYTES, 0.72);
      images.push(compressed);
    } catch (err) {
      console.warn('[BugReporter] Failed to process manual attachment:', attachment.name, err);
    }
  }

  return images;
}
